import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
} from "@angular/core";
import { AbstractControl, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { AssessmentConfigRes, ManeuverOutcomeDraft, OBAttempt } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { SharedService } from "src/app/shared/services/shared.service";
import { ContainerRatingsService } from "../../../services/base/container-ratings.services";
import {
	areManeuverFormObTotalsZero,
	captureManeuverRepeatBaselineOnce,
	resolveManeuverRepeatSnapshot,
	getObCellControl,
	getTs1V3ObUndoBaseline,
	getTs1V3ObUndoBaselinesRecord,
	obDraftStoreKeyForRole,
	ObRoleSlot,
	setTs1V3ObUndoBaseline,
	updateManeuverRepeatBaselineAfterRepeat,
} from "../../../shared-v2/utils/observation.utils";
import {
	applyPreDefinedFlagsFromConfig,
	buildConditionalObKeySet,
	buildPredefinedObKeySet,
	isConditionalObKey,
	isPredefinedObAttempt,
	isPredefinedObKey,
	TS1_CONDITIONAL_OB_BADGE_SRC,
	TS1_CONDITIONAL_OB_BADGE_TOOLTIP,
	TS1_PREDEFINED_OB_BADGE_TOOLTIP,
} from "../../utils/ts1-predefined-ob.utils";
import {
	applyManeuverRepeat,
	buildObservationCode,
	computeObRemoveValue,
	handleObAdd,
	handleObObservationComment,
	handleObRemove,
	handleObReset,
	handleObToggleNA,
	obCanUndoOneStepForCell,
	Ts1RatingObContext,
} from "../../utils/ts1-rating-ob-interaction";
import {
	isObColumnLockedByOutcome,
	maneuverOutcomeHasActivityForCandidate,
} from "../../utils/ts1-rating-outcome-interaction";

interface RepeatCandidateModalOption {
	candidateId: string;
	label: string;
	disabled?: boolean;
}

type ObCellSide = "left" | "right";

interface PointerStart {
	x: number;
	y: number;
	dragging: boolean;
}

@Component({
	selector: "app-ts1-rating-ob-panel",
	templateUrl: "./ts1-rating-ob-panel.component.html",
	styleUrls: ["./ts1-rating-ob-panel.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingObPanelComponent implements OnChanges, OnDestroy {
	@Input() leftManeuverForm: UntypedFormGroup | null = null;
	@Input() rightManeuverForm: UntypedFormGroup | null = null;
	@Input() leftSlot: ObRoleSlot = "pf";
	@Input() rightSlot: ObRoleSlot = "pm";
	@Input() leftCandidateRole: CandidateRole = CandidateRole.PF;
	@Input() rightCandidateRole: CandidateRole = CandidateRole.PM;
	@Input() leftCandidateName = "";
	@Input() rightCandidateName = "";
	@Input() leftColumnEnabled = true;
	@Input() rightColumnEnabled = true;
	@Input() hasSecondCandidate = false;
	@Input() formTypeCode = CONSTANT.FORM_TYPE.ts_1;
	@Input() activeManeuverOutcome: ManeuverOutcomeDraft | null = null;
	@Input() assessmentConfig: AssessmentConfigRes[] = [];
	@Input() obCommentsEnabled = true;
	@Input() showConditionalObHighlight = false;

	@Output() obComment = new EventEmitter<unknown>();
	@Output() repeatApplied = new EventEmitter<{ maneuverCode: string; candidateIds: string[] }>();

	readonly MAX = CONSTANT.MAX_OB_ATTEMPT_LIMIT;
	readonly conditionalObBadgeSrc = TS1_CONDITIONAL_OB_BADGE_SRC;
	readonly predefinedObBadgeTooltip = TS1_PREDEFINED_OB_BADGE_TOOLTIP;
	readonly conditionalObBadgeTooltip = TS1_CONDITIONAL_OB_BADGE_TOOLTIP;
	readonly OB_LAST_ACTION = CONSTANT.OB_LAST_ACTION;
	TS1_OB_LIST: Array<{ title: string; name: string; item_desc: Array<{ description: string }> }> = [];

	private initialObByKey: Record<string, OBAttempt> = {};
	private canUndoRevision = 0;
	private actionModeCells = new Set<string>();
	private pointerStarts = new Map<string, PointerStart>();
	private swipeOffsets = new Map<string, number>();
	private swipeRevision = 0;
	private isDialogOpen = false;
	/** Explicit expand overrides; absent keys fall back to value-driven expand. */
	private sectionExpandOverrides = new Map<string, boolean>();
	/** Skip expandedChange writes while expand/collapse-all is applying. */
	private suppressExpandedChange = false;
	/** Disables Material CSS transitions during expand/collapse-all to avoid jank. */
	bulkToggling = false;
	/** Bumped when predefined-star visibility is refreshed (template binding). */
	predefinedStarRevision = 0;
	private predefinedObKeysRevision = 0;
	private predefinedObKeysByManeuver = new Map<string, Set<string>>();
	private conditionalObKeysRevision = 0;
	private conditionalObKeysByManeuver = new Map<string, Set<string>>();

	private readonly swipeThresholdPx = 40;

	constructor(
		public sharedService: SharedService,
		private dialog: MatDialog,
		private containerRatingsService: ContainerRatingsService,
		private cd: ChangeDetectorRef,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes["formTypeCode"] ||
			changes["leftManeuverForm"] ||
			changes["rightManeuverForm"] ||
			changes["leftSlot"] ||
			changes["rightSlot"] ||
			changes["leftCandidateRole"] ||
			changes["rightCandidateRole"] ||
			changes["assessmentConfig"]
		) {
			const maneuverFormsChanged =
				(changes["leftManeuverForm"] && !changes["leftManeuverForm"].firstChange) ||
				(changes["rightManeuverForm"] && !changes["rightManeuverForm"].firstChange);
			if (maneuverFormsChanged) {
				this.clearSectionExpandOverrides();
			}
			this.loadObList();
			this.syncPreDefinedFromAssessmentConfig();
			this.captureInitialSnapshots();
			this.refreshCanUndoState();
			this.closeAllActionModes();
			this.refreshPredefinedObKeys();
			this.refreshConditionalObKeys();
			this.predefinedStarRevision++;
			this.cd.markForCheck();
		}

		if (changes["showConditionalObHighlight"]) {
			this.cd.markForCheck();
		}

		if (changes["activeManeuverOutcome"]) {
			this.closeAllActionModes();
			this.cd.detectChanges();
		}
	}

	/** Force re-render after external OB form patches (e.g. Maneuver Outcome N/A cascade). */
	refreshView(): void {
		this.closeAllActionModes();
		this.cd.detectChanges();
	}

	ngOnDestroy(): void {
		this.pointerStarts.clear();
		this.swipeOffsets.clear();
		this.actionModeCells.clear();
	}

	@HostListener("document:click", ["$event"])
	onDocumentClick(ev: MouseEvent): void {
		const target = ev.target as HTMLElement | null;
		if (target?.closest(".ts1-ob-cell")) return;
		this.closeAllActionModes();
	}

	@HostListener("document:contextmenu", ["$event"])
	onDocumentContextMenu(ev: MouseEvent): void {
		const target = ev.target as HTMLElement | null;
		if (target?.closest(".ts1-ob-cell")) return;
		this.closeAllActionModes();
	}

	private loadObList(): void {
		this.TS1_OB_LIST = this.sharedService.generateSortedOBList(this.formTypeCode) ?? [];
	}

	private captureInitialSnapshots(): void {
		this.captureManeuverSnapshot(this.leftManeuverForm);
		this.captureManeuverSnapshot(this.rightManeuverForm);
		this.captureFormSnapshots(this.leftManeuverForm, this.leftSlot, this.leftCandidateRole);
		this.captureFormSnapshots(this.rightManeuverForm, this.rightSlot, this.rightCandidateRole);
	}

	private captureManeuverSnapshot(form: UntypedFormGroup | null): void {
		if (!form) return;
		captureManeuverRepeatBaselineOnce(form);
	}

	get repeatDisabled(): boolean {
		return this.buildRepeatCandidateOptions().every((option) => option.disabled);
	}

	repeatConfirmModal(): void {
		if (this.repeatDisabled || this.isDialogOpen) return;

		const options = this.buildRepeatCandidateOptions();
		const activeOptions = options.filter((option) => !option.disabled);
		if (!activeOptions.length) return;

		this.isDialogOpen = true;

		const useCandidatePicker = this.hasSecondCandidate && activeOptions.length > 1;
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: useCandidatePicker ? "280px" : "200px",
			width: useCandidatePicker ? "420px" : "360px",
			hasBackdrop: true,
			data: useCandidatePicker
				? {
						modalName: "repeatCandidateModal",
						modalTitle: "Confirm repeat action!",
						modalContent: {
							message: "Are you sure to repeat this Maneuver?",
							options,
							selectedCandidateId: activeOptions.length === 1 ? activeOptions[0].candidateId : "",
						},
						modalBtnConfirmText: "Yes",
						modalBtnCancelText: "No",
					}
				: {
						modalTitle: "Confirm repeat action!",
						modalContent: "Are you sure to repeat this Maneuver?",
						modalBtnConfirmText: "Yes",
						modalBtnCancelText: "No",
					},
		});

		dialogRef.afterClosed().subscribe((result: { close?: boolean; candidateId?: string } | undefined) => {
			this.isDialogOpen = false;
			if (!result?.close) return;
			const candidateId = useCandidatePicker ? result.candidateId : activeOptions[0]?.candidateId;
			if (candidateId) {
				this.applyRepeatForCandidate(candidateId);
			}
		});
	}

	private buildRepeatCandidateOptions(): RepeatCandidateModalOption[] {
		const options: RepeatCandidateModalOption[] = [];

		if (this.leftManeuverForm) {
			const candidateId = String(this.leftManeuverForm.get("candidateId")?.value ?? "");
			if (candidateId) {
				options.push({
					candidateId,
					label: `Candidate 1 · ${this.leftCandidateName} · ${this.repeatCandidateRoleLabel(this.leftCandidateRole)}`,
					disabled: !this.candidateHasRepeatActivity(0, this.leftManeuverForm),
				});
			}
		}

		if (this.hasSecondCandidate && this.rightColumnEnabled && this.rightManeuverForm) {
			const candidateId = String(this.rightManeuverForm.get("candidateId")?.value ?? "");
			if (candidateId) {
				options.push({
					candidateId,
					label: `Candidate 2 · ${this.rightCandidateName} · ${this.repeatCandidateRoleLabel(this.rightCandidateRole)}`,
					disabled: !this.candidateHasRepeatActivity(1, this.rightManeuverForm),
				});
			}
		}

		return options;
	}

	private repeatCandidateRoleLabel(role: CandidateRole): string {
		return role === CandidateRole.PM ? "PM" : "PF";
	}

	private candidateHasRepeatActivity(candidateIndex: number, form: UntypedFormGroup): boolean {
		const obActive = !areManeuverFormObTotalsZero(form);
		const outcomeActive = maneuverOutcomeHasActivityForCandidate(
			this.activeManeuverOutcome,
			candidateIndex,
			this.hasSecondCandidate,
		);
		return obActive || outcomeActive;
	}

	private applyRepeatForCandidate(candidateId: string): void {
		this.closeAllActionModes();

		const maneuverCode =
			this.leftManeuverForm?.get("maneuverCode")?.value ?? this.rightManeuverForm?.get("maneuverCode")?.value ?? "";

		const leftId = String(this.leftManeuverForm?.get("candidateId")?.value ?? "");
		const rightId = String(this.rightManeuverForm?.get("candidateId")?.value ?? "");

		let repeated = false;
		if (leftId === candidateId && this.leftManeuverForm) {
			repeated = this.repeatManeuverForm(this.leftManeuverForm, this.leftSlot, this.leftCandidateRole, true);
		} else if (rightId === candidateId && this.rightManeuverForm) {
			repeated = this.repeatManeuverForm(this.rightManeuverForm, this.rightSlot, this.rightCandidateRole, true);
		}

		if (repeated && maneuverCode) {
			this.repeatApplied.emit({ maneuverCode, candidateIds: [candidateId] });
		}

		this.refreshCanUndoState();
		this.cd.detectChanges();
	}

	private repeatManeuverForm(
		form: UntypedFormGroup | null,
		roleSlot: ObRoleSlot,
		candidateRole: CandidateRole,
		candidateScoped = true,
	): boolean {
		if (!form) return false;
		const snapshot = resolveManeuverRepeatSnapshot(form);
		if (!snapshot) return false;

		applyManeuverRepeat({
			maneuverForm: form,
			initialSnapshot: snapshot,
			containerRatingsService: this.containerRatingsService,
			onCommentEmitted: (comment) => this.obComment.emit(comment),
			roleSlot,
			candidateRole,
			suppressSummaryEmit: true,
			candidateScoped,
		});

		updateManeuverRepeatBaselineAfterRepeat(form);
		this.forceRecaptureFormSnapshots(form);
		this.predefinedStarRevision++;
		return true;
	}

	/** Recapture undo baselines for both PF and PM after a maneuver-wide Repeat. */
	private forceRecaptureFormSnapshots(form: UntypedFormGroup): void {
		const candidateId = form.get("candidateId")?.value;
		const maneuverCode = form.get("maneuverCode")?.value;
		if (!candidateId || !maneuverCode) return;

		const prefix = `${candidateId}.${maneuverCode}.`;
		for (const key of Object.keys(this.initialObByKey)) {
			if (key.startsWith(prefix)) {
				delete this.initialObByKey[key];
			}
		}

		const roles: Array<{ slot: ObRoleSlot; role: CandidateRole }> = [
			{ slot: "pf", role: CandidateRole.PF },
			{ slot: "pm", role: CandidateRole.PM },
		];

		for (const { slot, role } of roles) {
			for (const ob of this.TS1_OB_LIST) {
				for (let i = 0; i < ob.item_desc.length; i++) {
					const ctrl = this.getObControl(form, ob.title, i, slot);
					if (!ctrl) continue;
					const obLabel = this.getObLabel(ob.title, i);
					const key = obDraftStoreKeyForRole(
						candidateId,
						maneuverCode,
						buildObservationCode(obLabel.toLowerCase()),
						role,
					);
					const snapshot = this.normalizePredefinedObAttempt(ctrl.value as OBAttempt);
					this.initialObByKey[key] = structuredClone(snapshot);
					setTs1V3ObUndoBaseline(key, snapshot);
				}
			}
		}
	}

	private captureFormSnapshots(
		form: UntypedFormGroup | null,
		roleSlot: ObRoleSlot,
		candidateRole: CandidateRole,
	): void {
		if (!form) return;
		const candidateId = form.get("candidateId")?.value;
		const maneuverCode = form.get("maneuverCode")?.value;
		if (!candidateId || !maneuverCode) return;

		for (const ob of this.TS1_OB_LIST) {
			for (let i = 0; i < ob.item_desc.length; i++) {
				const ctrl = this.getObControl(form, ob.title, i, roleSlot);
				if (!ctrl) continue;
				const obLabel = this.getObLabel(ob.title, i);
				const key = obDraftStoreKeyForRole(
					candidateId,
					maneuverCode,
					buildObservationCode(obLabel.toLowerCase()),
					candidateRole,
				);
				if (!(key in this.initialObByKey)) {
					const stored = getTs1V3ObUndoBaseline(key);
					const snapshot = this.normalizePredefinedObAttempt((stored ?? (ctrl.value as OBAttempt)) as OBAttempt);
					this.initialObByKey[key] = structuredClone(snapshot);
				}
			}
		}
	}

	private refreshCanUndoState(): void {
		this.canUndoRevision++;
	}

	isActionMode(form: UntypedFormGroup | null, obLabel: string, side: ObCellSide): boolean {
		if (!form) return false;
		return this.actionModeCells.has(this.cellKey(form, obLabel, side));
	}

	showActionMode(form: UntypedFormGroup, obLabel: string, side: ObCellSide): void {
		this.closeAllActionModes();
		const key = this.cellKey(form, obLabel, side);
		this.actionModeCells.add(key);
		this.swipeOffsets.delete(key);
		this.bumpSwipeRevision();
		this.cd.markForCheck();
	}

	closeActionMode(form: UntypedFormGroup | null, obLabel: string, side: ObCellSide): void {
		if (!form) return;
		const key = this.cellKey(form, obLabel, side);
		this.actionModeCells.delete(key);
		this.swipeOffsets.delete(key);
		this.pointerStarts.delete(key);
		this.cd.markForCheck();
	}

	toggleActionMode(form: UntypedFormGroup, obLabel: string, side: ObCellSide): void {
		if (this.isActionMode(form, obLabel, side)) {
			this.closeActionMode(form, obLabel, side);
		} else {
			this.showActionMode(form, obLabel, side);
		}
	}

	isDragging(form: UntypedFormGroup | null, obLabel: string, side: ObCellSide): boolean {
		if (!form) return false;
		return this.pointerStarts.get(this.cellKey(form, obLabel, side))?.dragging ?? false;
	}

	isActionsMuted(
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		control: AbstractControl | null,
		role: CandidateRole,
	): boolean {
		if (!form) return false;
		if (this.isActionMode(form, obLabel, side)) return true;
		const offset = this.getActionsOffsetPx(form, obLabel, side, control, role);
		return offset < -8;
	}

	getRevealVisibleWidth(
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		control: AbstractControl | null,
		role: CandidateRole,
	): number {
		void this.swipeRevision;
		if (!form) return 0;
		const revealWidth = this.getRevealWidthPxInternal(form, obLabel, role, control);
		const offset = this.getActionsOffsetPx(form, obLabel, side, control, role);
		return Math.min(revealWidth, Math.max(0, Math.abs(offset)));
	}

	getActionsTransform(
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		control: AbstractControl | null,
		role: CandidateRole,
	): string {
		const offset = this.getActionsOffsetPx(form, obLabel, side, control, role);
		return `translateX(${offset}px)`;
	}

	private getActionsOffsetPx(
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		control: AbstractControl | null,
		role: CandidateRole,
	): number {
		void this.swipeRevision;
		if (!form) return 0;
		const key = this.cellKey(form, obLabel, side);
		const revealWidth = this.getRevealWidthPxInternal(form, obLabel, role, control);

		if (this.swipeOffsets.has(key)) {
			return this.clampSwipeOffset(this.swipeOffsets.get(key)!, revealWidth);
		}

		return this.isActionMode(form, obLabel, side) ? -revealWidth : 0;
	}

	getRevealWidthPx(
		form: UntypedFormGroup | null,
		obLabel: string,
		role: CandidateRole,
		control: AbstractControl | null,
	): number {
		if (!form) return 0;
		return this.getRevealWidthPxInternal(form, obLabel, role, control);
	}

	private getRevealWidthPxInternal(
		form: UntypedFormGroup,
		obLabel: string,
		role: CandidateRole,
		control: AbstractControl | null,
	): number {
		const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
		const btnWidth = isDesktop ? 44 : 36;
		const gap = isDesktop ? 10 : 8;
		const edgePad = isDesktop ? 10 : 8;
		const hasComment = this.shouldShowObservationComment(form, control, obLabel, role);
		const hasNa = this.shouldShowPredefinedStar(form, control, obLabel, role);
		const hasUndo = this.showUndo(form, obLabel, role) && !!control;
		const btnCount = (hasComment ? 1 : 0) + (hasNa ? 1 : 0) + (hasUndo ? 1 : 0);
		if (btnCount === 0) return 0;
		return edgePad + btnCount * btnWidth + Math.max(0, btnCount - 1) * gap;
	}

	private clampSwipeOffset(offset: number, revealWidth: number): number {
		return Math.max(-revealWidth, Math.min(0, offset));
	}

	private bumpSwipeRevision(): void {
		this.swipeRevision++;
	}

	private closeAllActionModes(): void {
		if (this.actionModeCells.size === 0 && this.swipeOffsets.size === 0) return;
		this.actionModeCells.clear();
		this.swipeOffsets.clear();
		this.pointerStarts.clear();
		this.cd.markForCheck();
	}

	showUndo(form: UntypedFormGroup | null, obLabel: string, role: CandidateRole): boolean {
		void this.canUndoRevision;
		if (!form) return false;
		return obCanUndoOneStepForCell(this.buildCtx(form, obLabel, role));
	}

	isSectionExpanded(title: string): boolean {
		if (this.sectionExpandOverrides.has(title)) {
			return this.sectionExpandOverrides.get(title)!;
		}
		return this.hasSectionValue(title);
	}

	onSectionExpandedChange(title: string, expanded: boolean): void {
		if (this.suppressExpandedChange) return;
		this.sectionExpandOverrides.set(title, expanded);
		this.cd.markForCheck();
	}

	expandAllSections(): void {
		this.applyAllSectionsExpanded(true);
	}

	collapseAllSections(): void {
		this.applyAllSectionsExpanded(false);
	}

	private applyAllSectionsExpanded(expanded: boolean): void {
		this.suppressExpandedChange = true;

		// Disable transitions first, then flip expanded — avoids mid-flight CSS jank.
		this.bulkToggling = true;
		this.cd.detectChanges();

		for (const section of this.TS1_OB_LIST) {
			this.sectionExpandOverrides.set(section.title, expanded);
		}
		this.cd.detectChanges();

		// Re-enable transitions only after the browser has painted the bulk state.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.bulkToggling = false;
				this.suppressExpandedChange = false;
				this.cd.markForCheck();
			});
		});
	}

	private clearSectionExpandOverrides(): void {
		this.sectionExpandOverrides.clear();
	}

	hasSectionValue(title: string): boolean {
		return (
			this.hasValueInForm(this.leftManeuverForm, title, this.leftSlot, this.leftColumnEnabled) ||
			this.hasValueInForm(this.rightManeuverForm, title, this.rightSlot, this.rightColumnEnabled)
		);
	}

	private hasValueInForm(
		form: UntypedFormGroup | null,
		title: string,
		roleSlot: ObRoleSlot,
		columnEnabled: boolean,
	): boolean {
		if (!columnEnabled) return false;
		const section = this.TS1_OB_LIST.find((ob) => ob.title === title);
		if (!section) return false;
		for (let i = 0; i < section.item_desc.length; i++) {
			const ctrl = this.getObControl(form, title, i, roleSlot);
			const value = this.getObValue(ctrl);
			if (value.isDisabled) continue;
			if ((value.currentCount ?? 0) > 0) return true;
		}
		return false;
	}

	getObLabel(title: string, index: number): string {
		return `${title} ${index + 1}`;
	}

	getObControl(
		form: UntypedFormGroup | null,
		title: string,
		index: number,
		roleSlot: ObRoleSlot,
	): AbstractControl | null {
		if (!form) return null;
		return getObCellControl(form, title, index + 1, roleSlot);
	}

	getObValue(control: AbstractControl | null): OBAttempt {
		return (control?.value ?? {
			totalCount: 0,
			currentCount: 0,
			preDefined: false,
			lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
			isDisabled: false,
		}) as OBAttempt;
	}

	hasCount(control: AbstractControl | null): boolean {
		const v = this.getObValue(control);
		return !v.isDisabled && (v.currentCount ?? 0) > 0;
	}

	/** True when the OB cell itself is marked N/A (form state — drives toggle/calc). */
	isObNaDisabled(control: AbstractControl | null): boolean {
		return !!this.getObValue(control).isDisabled;
	}

	/** Visual N/A style: cell isDisabled or column locked by Maneuver Outcome N/A. */
	isGreyed(control: AbstractControl | null, side: ObCellSide = "left"): boolean {
		if (this.isObNaDisabled(control)) return true;
		return isObColumnLockedByOutcome(
			this.activeManeuverOutcome,
			side,
			side === "left" ? this.leftCandidateRole : this.rightCandidateRole,
			this.hasSecondCandidate,
		);
	}

	/** columnEnabled and not locked by Maneuver Outcome N/A for that role. */
	isColumnInteractive(columnEnabled: boolean, side: ObCellSide): boolean {
		if (!columnEnabled) return false;
		const role = side === "left" ? this.leftCandidateRole : this.rightCandidateRole;
		return !isObColumnLockedByOutcome(this.activeManeuverOutcome, side, role, this.hasSecondCandidate);
	}

	cellStateClass(
		control: AbstractControl | null,
		columnEnabled: boolean,
		form: UntypedFormGroup | null,
		obLabel: string,
		role: CandidateRole,
		side: ObCellSide = "left",
	): Record<string, boolean> {
		if (!columnEnabled) {
			return { "ts1-ob-cell--column-disabled": true };
		}
		const v = this.getObValue(control);
		const lastAction = v.lastAction ?? this.OB_LAST_ACTION.NO_ACTION;
		const greyed = this.isGreyed(control, side);
		return {
			"ts1-ob-cell--active":
				!greyed &&
				(v.currentCount ?? 0) > 0 &&
				lastAction !== this.OB_LAST_ACTION.ADDED &&
				lastAction !== this.OB_LAST_ACTION.REMOVED,
			"ts1-ob-cell--added": !greyed && lastAction === this.OB_LAST_ACTION.ADDED,
			"ts1-ob-cell--removed": !greyed && lastAction === this.OB_LAST_ACTION.REMOVED,
			"ts1-ob-cell--greyed": greyed,
		};
	}

	shouldHighlightConditionalOb(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
	): boolean {
		if (!this.showConditionalObHighlight) return false;
		void this.conditionalObKeysRevision;

		const maneuverCode = this.resolveManeuverCodeForStar(form, control);
		if (!maneuverCode) return false;

		return isConditionalObKey(this.getConditionalObKeys(maneuverCode), obLabel, role);
	}

	disableAdd(control: AbstractControl | null, columnEnabled = true, side: ObCellSide = "left"): boolean {
		if (!this.isColumnInteractive(columnEnabled, side)) return true;
		const v = this.getObValue(control);
		if (v.isDisabled) return true;
		return v.totalCount >= this.MAX;
	}

	disableRemove(control: AbstractControl | null, columnEnabled = true, side: ObCellSide = "left"): boolean {
		if (!this.isColumnInteractive(columnEnabled, side)) return true;
		const v = this.getObValue(control);
		if (v.isDisabled) return true;
		return computeObRemoveValue(v) === null;
	}

	/** Lock +/- while swiping or when N/A / comment / undo actions are revealed. */
	isAttemptButtonsSwipeLocked(
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		control: AbstractControl | null,
		role: CandidateRole,
	): boolean {
		if (!form) return false;
		return this.isDragging(form, obLabel, side) || this.isActionsMuted(form, obLabel, side, control, role);
	}

	shouldDisableAdd(
		control: AbstractControl | null,
		columnEnabled: boolean,
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		role: CandidateRole,
	): boolean {
		return (
			this.disableAdd(control, columnEnabled, side) ||
			this.isAttemptButtonsSwipeLocked(form, obLabel, side, control, role)
		);
	}

	shouldDisableRemove(
		control: AbstractControl | null,
		columnEnabled: boolean,
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		role: CandidateRole,
	): boolean {
		return (
			this.disableRemove(control, columnEnabled, side) ||
			this.isAttemptButtonsSwipeLocked(form, obLabel, side, control, role)
		);
	}

	private buildCtx(form: UntypedFormGroup, obLabel: string, role: CandidateRole): Ts1RatingObContext {
		const candidateName =
			form === this.leftManeuverForm
				? this.leftCandidateName
				: form === this.rightManeuverForm
					? this.rightCandidateName
					: "";
		return {
			maneuverForm: form,
			obLabel,
			candidateRole: role,
			candidateName,
			dialog: this.dialog,
			containerRatingsService: this.containerRatingsService,
			initialObByKey: this.initialObByKey,
			obCommentsEnabled: this.obCommentsEnabled,
			onCommentEmitted: (c) => this.obComment.emit(c),
			onDraftUpdated: () => this.refreshCanUndoState(),
		};
	}

	onAdd(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (
			!this.isColumnInteractive(columnEnabled, side) ||
			!form ||
			!control ||
			this.isObNaDisabled(control) ||
			this.isAttemptButtonsSwipeLocked(form, obLabel, side, control, role)
		) {
			return;
		}
		this.closeActionMode(form, obLabel, side);
		handleObAdd(this.buildCtx(form, obLabel, role), control, this.getObValue(control));
	}

	onRemove(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (
			!this.isColumnInteractive(columnEnabled, side) ||
			!form ||
			!control ||
			this.isObNaDisabled(control) ||
			this.isAttemptButtonsSwipeLocked(form, obLabel, side, control, role)
		) {
			return;
		}
		this.closeActionMode(form, obLabel, side);
		handleObRemove(this.buildCtx(form, obLabel, role), control, this.getObValue(control));
	}

	onUndo(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (!this.isColumnInteractive(columnEnabled, side) || !form || !control) return;
		if (this.isDialogOpen) return;

		this.isDialogOpen = true;
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: "This will undo the last action only. Do you want to continue?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		dialogRef.afterClosed().subscribe((result: { close?: boolean } | undefined) => {
			this.isDialogOpen = false;
			if (result?.close) {
				handleObReset(this.buildCtx(form, obLabel, role), control, this.getObValue(control));
				this.closeActionMode(form, obLabel, side);
				this.cd.detectChanges();
			}
		});
	}

	onNA(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (!this.isColumnInteractive(columnEnabled, side) || !form || !control) return;
		if (!this.shouldShowPredefinedStar(form, control, obLabel, role)) return;
		this.confirmToggleNA(form, control, obLabel, role, side);
	}

	onObservationComment(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (!this.isColumnInteractive(columnEnabled, side) || !form || !control || this.isObNaDisabled(control)) return;
		if (!this.shouldShowObservationComment(form, control, obLabel, role)) return;
		if (this.isDialogOpen) return;
		this.closeActionMode(form, obLabel, side);
		handleObObservationComment(this.buildCtx(form, obLabel, role), control);
	}

	onCellPointerDown(
		ev: PointerEvent,
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (!this.isColumnInteractive(columnEnabled, side) || ev.button !== 0 || !form || !control) return;
		if (this.isFromInteractive(ev)) return;

		const key = this.cellKey(form, obLabel, side);
		this.pointerStarts.set(key, { x: ev.clientX, y: ev.clientY, dragging: false });
		(ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
	}

	onCellPointerMove(
		ev: PointerEvent,
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		side: ObCellSide,
	): void {
		if (!form || !control) return;
		const key = this.cellKey(form, obLabel, side);
		const start = this.pointerStarts.get(key);
		if (!start) return;

		const deltaX = ev.clientX - start.x;
		const deltaY = ev.clientY - start.y;

		if (!start.dragging) {
			if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
			start.dragging = true;
		}

		const revealWidth = this.getRevealWidthPxInternal(form, obLabel, role, control);
		const baseOffset = this.isActionMode(form, obLabel, side) ? -revealWidth : 0;
		this.swipeOffsets.set(key, this.clampSwipeOffset(baseOffset + deltaX, revealWidth));
		this.bumpSwipeRevision();
		this.cd.markForCheck();
	}

	onCellPointerUp(
		ev: PointerEvent,
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		if (!form || !control) return;
		const key = this.cellKey(form, obLabel, side);
		const start = this.pointerStarts.get(key);
		this.pointerStarts.delete(key);
		(ev.currentTarget as HTMLElement)?.releasePointerCapture?.(ev.pointerId);

		if (!start || !this.isColumnInteractive(columnEnabled, side) || this.isFromInteractive(ev)) {
			this.swipeOffsets.delete(key);
			this.bumpSwipeRevision();
			return;
		}

		const deltaX = ev.clientX - start.x;
		const deltaY = ev.clientY - start.y;
		const revealWidth = this.getRevealWidthPxInternal(form, obLabel, role, control);
		const currentOffset = this.swipeOffsets.get(key) ?? (this.isActionMode(form, obLabel, side) ? -revealWidth : 0);
		this.swipeOffsets.delete(key);
		this.bumpSwipeRevision();

		const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
		if (start.dragging && isHorizontal) {
			if (revealWidth <= 0) {
				this.closeActionMode(form, obLabel, side);
				return;
			}
			if (deltaX <= -this.swipeThresholdPx) {
				this.showActionMode(form, obLabel, side);
				return;
			}
			if (deltaX >= this.swipeThresholdPx) {
				this.closeActionMode(form, obLabel, side);
				return;
			}
			if (currentOffset <= -revealWidth / 2) {
				this.showActionMode(form, obLabel, side);
				return;
			}
			this.closeActionMode(form, obLabel, side);
			return;
		}

		this.cd.markForCheck();
	}

	onCellPointerCancel(
		form: UntypedFormGroup | null,
		obLabel: string,
		side: ObCellSide,
		control: AbstractControl | null,
		role: CandidateRole,
	): void {
		if (!form) return;
		const key = this.cellKey(form, obLabel, side);
		this.pointerStarts.delete(key);
		if (this.swipeOffsets.has(key)) {
			const revealWidth = this.getRevealWidthPxInternal(form, obLabel, role, control);
			const currentOffset = this.swipeOffsets.get(key)!;
			this.swipeOffsets.delete(key);
			this.bumpSwipeRevision();
			if (revealWidth > 0 && currentOffset <= -revealWidth / 2) {
				this.showActionMode(form, obLabel, side);
			} else {
				this.closeActionMode(form, obLabel, side);
			}
		}
	}

	onCellContextMenu(
		ev: MouseEvent,
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
		columnEnabled = true,
		side: ObCellSide = "left",
	): void {
		ev.preventDefault();
		ev.stopPropagation();
		if (!this.isColumnInteractive(columnEnabled, side) || !form || !control) return;
		this.pointerStarts.delete(this.cellKey(form, obLabel, side));
		this.swipeOffsets.delete(this.cellKey(form, obLabel, side));
		if (this.getRevealWidthPxInternal(form, obLabel, role, control) <= 0) return;
		this.toggleActionMode(form, obLabel, side);
	}

	private confirmToggleNA(
		form: UntypedFormGroup,
		control: AbstractControl,
		obLabel: string,
		role: CandidateRole,
		side: ObCellSide,
	): void {
		const current = this.getObValue(control);
		if (this.isDialogOpen) return;

		// Use form isDisabled only — not visual greying from Maneuver Outcome N/A on another column.
		const willGrey = !this.isObNaDisabled(control);
		const message = willGrey ? "Are you sure to mark this OB as N/A?" : "Enable this card again?";

		this.isDialogOpen = true;
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: message,
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		dialogRef.afterClosed().subscribe((result: { close?: boolean } | undefined) => {
			this.isDialogOpen = false;
			if (result?.close) {
				handleObToggleNA(this.buildCtx(form, obLabel, role), control, current, willGrey);
				this.closeActionMode(form, obLabel, side);
				this.cd.detectChanges();
			}
		});
	}

	private cellKey(form: UntypedFormGroup, obLabel: string, side: ObCellSide): string {
		return `${form.get("candidateId")?.value}::${side}::${obLabel}`;
	}

	private sideForForm(form: UntypedFormGroup): ObCellSide {
		return form === this.leftManeuverForm ? "left" : "right";
	}

	private isFromInteractive(ev: Event): boolean {
		const el = ev.target as HTMLElement | null;
		if (!el) return false;
		return !!el.closest("button, [role='button'], a, input, textarea, select, .attemptBtn, .actionBtn, .ts1-ob-info");
	}

	private resolveManeuverCodeForStar(form: UntypedFormGroup | null, control: AbstractControl | null): string {
		const fromForm = (form?.get("maneuverCode")?.value as string | undefined)?.trim();
		if (fromForm) return fromForm;

		const fromAttempt = (this.getObValue(control).fixedManeuverCode as string | undefined)?.trim();
		return fromAttempt ?? "";
	}

	shouldShowPredefinedStar(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
	): boolean {
		void this.predefinedObKeysRevision;
		void this.predefinedStarRevision;

		const maneuverCode = this.resolveManeuverCodeForStar(form, control);

		if (maneuverCode && isPredefinedObKey(this.getPredefinedObKeys(maneuverCode), obLabel, role)) {
			return true;
		}

		if (isPredefinedObAttempt(this.getObValue(control))) return true;

		if (!form || !control) return false;

		const candidateId = form.get("candidateId")?.value;
		if (!candidateId || !maneuverCode) return false;

		const key = obDraftStoreKeyForRole(candidateId, maneuverCode, buildObservationCode(obLabel.toLowerCase()), role);
		const initial = this.initialObByKey[key];
		return initial ? isPredefinedObAttempt(initial) : false;
	}

	/** Predefined OBs always allow comments; free-form OBs only after +/- activation (totalCount > 0). */
	shouldShowObservationComment(
		form: UntypedFormGroup | null,
		control: AbstractControl | null,
		obLabel: string,
		role: CandidateRole,
	): boolean {
		if (this.shouldShowPredefinedStar(form, control, obLabel, role)) return true;
		return (this.getObValue(control).totalCount ?? 0) > 0;
	}

	/** Re-apply config flags and force star visibility refresh after async draft replay. */
	refreshPredefinedStars(): void {
		this.syncPreDefinedFromAssessmentConfig();
		this.refreshPredefinedObKeys();
		this.refreshConditionalObKeys();
		this.refreshUndoBaselinesFromStore();
		this.recaptureAllSnapshots();
		this.predefinedStarRevision++;
		this.cd.detectChanges();
	}

	private refreshUndoBaselinesFromStore(): void {
		for (const [key, value] of Object.entries(getTs1V3ObUndoBaselinesRecord())) {
			this.initialObByKey[key] = structuredClone(value);
		}
	}

	private recaptureAllSnapshots(): void {
		this.initialObByKey = { ...getTs1V3ObUndoBaselinesRecord() };
		this.captureInitialSnapshots();
	}

	private getPredefinedObKeys(maneuverCode: string): Set<string> {
		if (!this.predefinedObKeysByManeuver.has(maneuverCode)) {
			this.predefinedObKeysByManeuver.set(maneuverCode, buildPredefinedObKeySet(this.assessmentConfig, maneuverCode));
		}
		return this.predefinedObKeysByManeuver.get(maneuverCode) ?? new Set();
	}

	private refreshPredefinedObKeys(): void {
		this.predefinedObKeysByManeuver.clear();
		this.predefinedObKeysRevision++;
	}

	private getConditionalObKeys(maneuverCode: string): Set<string> {
		if (!this.conditionalObKeysByManeuver.has(maneuverCode)) {
			this.conditionalObKeysByManeuver.set(maneuverCode, buildConditionalObKeySet(this.assessmentConfig, maneuverCode));
		}
		return this.conditionalObKeysByManeuver.get(maneuverCode) ?? new Set();
	}

	private refreshConditionalObKeys(): void {
		this.conditionalObKeysByManeuver.clear();
		this.conditionalObKeysRevision++;
	}

	private syncPreDefinedFromAssessmentConfig(): void {
		if (!this.assessmentConfig.length) return;

		const forms = [this.leftManeuverForm, this.rightManeuverForm].filter(Boolean) as UntypedFormGroup[];
		const seen = new Set<string>();
		for (const form of forms) {
			const maneuverCode = form.get("maneuverCode")?.value as string;
			if (!maneuverCode || seen.has(maneuverCode)) continue;
			seen.add(maneuverCode);
			applyPreDefinedFlagsFromConfig(form, this.assessmentConfig, maneuverCode);
		}
	}

	private normalizePredefinedObAttempt(value: OBAttempt): OBAttempt {
		if (isPredefinedObAttempt(value)) {
			return { ...value, preDefined: true };
		}
		if (value.lastAction !== this.OB_LAST_ACTION.PRE_SELECTED) {
			return value;
		}
		return { ...value, preDefined: true };
	}
}
